import React, {
  FC,
  useMemo,
  useCallback,
  useEffect,
  useContext,
  useState,
  useRef,
} from 'react';

import { Link } from 'react-navi';
import { Trans, useTranslation } from 'react-i18next';
import { Form, FormApi, FormState, useFormState } from 'informed';
import {
  useApi,
  Results,
  TermsOfService,
  ProcessingTimeType,
  AuthContext,
  Payment,
  uuid,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';

import { ModalsContext } from 'contexts/modal';
import { Spinner } from 'components/Spinner';
import { Button } from 'components/buttons';
import { TooltipTypes } from 'modules/globalTooltip';
import { showTooltip } from 'helpers/tooltip';
import {
  configPayments,
  getMethodFields,
  submitFormPayment,
} from 'helpers/payments';
import { useAnalytics } from 'helpers/analytics';
import { translationFactory } from 'helpers/trans';
import { AnalyticsEventName } from 'api/analytics';
import { PaymentMethodContext, PaymentMethodContextProps } from './providers';
import {
  AccountTitleType,
  CRYPTO_METHOD,
  formatAccountTitle,
  formattingData,
  hideSensitiveData,
  methodFieldsPath,
  MethodFormApi2,
  paymentsStorage,
  scrollToFirstError,
} from './helpers';
import { PaymentField, PaymentFieldProps } from './fields';
import { ConfirmRemoveMethodModal } from './components/ConfirmRemoveMethodModal';
import { LocalStorageDeposit } from 'helpers/common';
import { groupMemberNoKYC } from 'helpers/user';

import { ScPaymentMethod } from './styled';
import { ScNotFound } from '../PaymentMethodsNotFound/styled';

type PaymentMethodProps = Pick<PaymentMethodContextProps, 'paymentMethod'> & {
  userCurrency: string;
  paymentAction: Payment.Action;
  currentPaymentMethod?: Payment.Method;
  setCurrentPaymentMethod?: (paymentMethod: Payment.Method) => void;
};

export const PaymentMethod: FC<PaymentMethodProps> = ({
  userCurrency,
  paymentMethod,
  paymentAction,
  setCurrentPaymentMethod,
}) => {
  const { t } = useTranslation(['deposits', 'cashbox', 'glossary']);
  const { user } = useContext(AuthContext);
  const {
    id,
    type,
    termsOfService: {
      restrictions: {
        amountCurrencyCode: currency,
        minAmountValue,
        maxAmountValue,
      },
    },
    savedProfiles,
  } = paymentMethod;

  const isCryptoMethod = type.includes(CRYPTO_METHOD);

  const [
    requestGetMethodFields,
    { data: methodFields, pending: pendingMethodFields },
  ] = useApi(getMethodFields);

  const [
    currentPrevUsedProfile,
    setCurrentPrevUsedProfile,
  ] = useState<Payment.SavedProfile>();

  const isDeposit = paymentAction === Payment.Action.Deposit;

  useEffect(() => {
    savedProfiles.length
      ? setCurrentPrevUsedProfile(savedProfiles[0])
      : setCurrentPrevUsedProfile(undefined);
  }, [savedProfiles, user]);

  useEffect(() => {
    requestGetMethodFields(
      {
        currency: userCurrency,
        id,
        savedProfileId: currentPrevUsedProfile?.id || undefined,
      },
      paymentAction
    );
  }, [
    currentPrevUsedProfile,
    id,
    paymentAction,
    requestGetMethodFields,
    userCurrency,
  ]);

  useEffect(() => {
    if (
      methodFields != null &&
      (methodFields as Results.GetMethodFieldsResult).methodFields == null
    ) {
    }
  }, [methodFields, currency, id]);

  const getAccountTitle = useCallback(
    (accountTitle: string) => {
      const { title, type } = formatAccountTitle(accountTitle);

      return type === AccountTitleType.CREDIT_CARD
        ? `${t('ending')} ${title}`
        : title;
    },
    [t]
  );

  return (
    <PaymentMethodContext.Provider
      value={{
        currency,
        paymentMethod,
        currentPrevUsedProfile,
        setCurrentPaymentMethod,
      }}
    >
      <ScPaymentMethod isCryptoMethod={isCryptoMethod && isDeposit}>
        <div className="title-wrapper">
          <h2>{paymentMethod.title}</h2>
          {isDeposit && !isCryptoMethod && (
            <div className="amount-value">
              <span className="instantly-text">{t('glossary:instantly')},</span>{' '}
              {minAmountValue && <>min {minAmountValue}</>}
              {maxAmountValue && <> - max {maxAmountValue}</>} {currency}
            </div>
          )}
        </div>
        {savedProfiles.length !== 0 && (
          <div className="profiles">
            {savedProfiles.map((profile, i) =>
              profile?.id ? (
                <div
                  className={`profile ${
                    profile.id === currentPrevUsedProfile?.id ? 'active' : ''
                  }`}
                  key={i}
                  onClick={() => setCurrentPrevUsedProfile(profile)}
                >
                  {getAccountTitle(profile.title)}
                </div>
              ) : null
            )}
            <div
              className={`profile ${
                currentPrevUsedProfile === undefined ? 'active' : ''
              }`}
              onClick={() => setCurrentPrevUsedProfile(undefined)}
            >
              {t('new')}
            </div>
          </div>
        )}
        {paymentAction === Payment.Action.Cashout && (
          <p className="permission">{t('cashbox:permission')}</p>
        )}
        {pendingMethodFields ? (
          <Spinner size={64} center />
        ) : methodFields ? (
          <MethodForm
            id={paymentMethod.id}
            userCurrency={userCurrency}
            currency={currency}
            paymentAction={paymentAction}
            prevUsedProfile={currentPrevUsedProfile}
            {...methodFields}
          />
        ) : (
          <>{t('common:notAvailable')}</>
        )}
      </ScPaymentMethod>
    </PaymentMethodContext.Provider>
  );
};

type MethodFields = Omit<Results.GetMethodFieldsResult, 'methodFields'> &
  Partial<Pick<Results.GetMethodFieldsResult, 'methodFields'>>;

interface MethodFormProps extends MethodFields {
  id: string;
  userCurrency: string;
  currency: string;
  paymentAction: Payment.Action;
  prevUsedProfile: Payment.SavedProfile | undefined;
}

const MethodForm: FC<MethodFormProps> = ({
  id,
  currency,
  methodFields,
  userCurrency,
  playerFields, // failed to reproduce work with this parameter
  amountField,
  isSubmitAvailable,
  paymentAction,
  prevUsedProfile,
}) => {
  const { fetchUser, setCurrentPaymentMethodId } = useContext(AuthContext);

  const [isConfirmRemoveModalOpen, toggleConfirmRemoveModal] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { sendEventToAnalytics } = useAnalytics();

  const { handleModal } = useContext(ModalsContext);
  const {
    paymentMethod: {
      type,
      termsOfService: {
        processingTime,
        restrictions: { minAmountValue, maxAmountValue },
      },
    },
  } = useContext(PaymentMethodContext);
  const { user, setSubmitPaymentPending } = useContext(AuthContext);

  const formApiRef = useRef<FormApi<MethodFormApi2>>();

  const [
    requestSubmitFormPayment,
    { data: responseSubmitFormPayment, pending: pendingFormPayment },
  ] = useApi(submitFormPayment);

  useEffect(() => {
    setSubmitPaymentPending(pendingFormPayment);
  }, [pendingFormPayment, setSubmitPaymentPending]);

  const isDeposit = paymentAction === Payment.Action.Deposit;

  useEffect(() => {
    if (id) {
      setCurrentPaymentMethodId(id);
    }
  }, [id, setCurrentPaymentMethodId]);

  useEffect(() => {
    const sendErrorEventToAnalytics = () => {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__FORM__ERROR,
        attributes: {
          additionalData: JSON.stringify(responseSubmitFormPayment),
        },
      });
    };

    if (responseSubmitFormPayment?.type === 'params_error') {
      if (
        responseSubmitFormPayment?.commonErrors != null &&
        responseSubmitFormPayment.commonErrors?.length !== 0
      ) {
        showTooltip({
          type: TooltipTypes.ERROR,
          title: responseSubmitFormPayment.commonErrors.join(', '),
        });
      }

      if (
        responseSubmitFormPayment?.amountFieldErrors != null &&
        responseSubmitFormPayment.amountFieldErrors?.length !== 0
      ) {
        formApiRef.current?.setError(
          'amountValue',
          responseSubmitFormPayment.amountFieldErrors.join(', ')
        );
        showTooltip({
          type: TooltipTypes.ERROR,
          title: responseSubmitFormPayment.amountFieldErrors.join(', '),
        });
      }
      if (
        responseSubmitFormPayment?.methodFieldsErrors != null &&
        responseSubmitFormPayment.methodFieldsErrors?.length !== 0
      ) {
        Object.entries(responseSubmitFormPayment.methodFieldsErrors).forEach(
          (error) => {
            formApiRef.current?.setError(
              `methodFieldsData.${error[0]}`,
              (error[1] as string[]).join('. ')
            );
          }
        );

        sendErrorEventToAnalytics();
        showTooltip({
          type: TooltipTypes.ERROR,
          translate: true,
          title: translationFactory('common:deposit.somethingWentWrong'),
        });
      }

      if (
        responseSubmitFormPayment?.commonErrors == null &&
        responseSubmitFormPayment.commonErrors?.length === 0 &&
        responseSubmitFormPayment?.amountFieldErrors != null &&
        responseSubmitFormPayment.amountFieldErrors?.length !== 0
      ) {
        showTooltip({
          type: TooltipTypes.ERROR,
          translate: true,
          title: translationFactory('common:deposit.somethingWentWrong'),
        });
      }
    }
  }, [responseSubmitFormPayment, sendEventToAnalytics]);

  const initialValues = useMemo(() => {
    const payments = paymentsStorage.get();

    if (payments == null || formApiRef == null || user?.currency == null)
      return;

    const currentPayment = payments[id];

    if (currentPayment == null) return;

    return currentPayment;
  }, [id, user?.currency]);

  const handleChange = useCallback(
    (values: FormState<MethodFormApi2>) => {
      const filteredValues = hideSensitiveData([
        { fieldPath: methodFieldsPath.cvvPath, toRemove: true },
        { fieldPath: methodFieldsPath.cvvField, toRemove: true },
        { fieldPath: methodFieldsPath.amountValue, toRemove: true },
      ])(values.values) as MethodFormApi2;

      paymentsStorage.push({ [id]: filteredValues });
    },
    [id]
  );

  const localPaymentMethod = LocalStorageDeposit.get().paymentMethod;

  const withdrawSubmitDisabled = useMemo(
    () =>
      !isDeposit &&
      localPaymentMethod?.type !== Payment.MethodType.Crypto &&
      groupMemberNoKYC(user),
    [isDeposit, localPaymentMethod?.type, user]
  );

  const isWithdraw = paymentAction === Payment.Action.Cashout;
  const isCryptoMethod = type.includes(CRYPTO_METHOD);

  const handleSubmitFailure = () => {
    const errors = formApiRef.current?.getState().errors ?? {};
    scrollToFirstError(errors);
  };

  const handleSubmit = useCallback(
    async (values: MethodFormApi2) => {
      const updatedValues = {
        ...values,
        ...(prevUsedProfile?.id && {
          savedProfileId: prevUsedProfile.id,
        }),
      };

      const formattedValues = formattingData(updatedValues);

      const data = { id, currency, ...formattedValues };

      if (!isDeposit) {
        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__WITHDRAW__FORM__REQUEST,
        });
      }

      const transactionUuid = uuid();

      await configPayments(language, transactionUuid);

      const { response } = await requestSubmitFormPayment(data, paymentAction)
        .request;

      if (response?.data?.type === 'params_error') return;

      if (isDeposit) {
        const analyticsData = hideSensitiveData([
          { fieldPath: methodFieldsPath.cvvPath, toRemove: true },
          { fieldPath: methodFieldsPath.cvvField, toRemove: true },
          {
            fieldPath: methodFieldsPath.creditCardNumberPath,
            replacer: (value) => value.slice(-4),
          },
        ])(data);

        LocalStorageDeposit.remove();

        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__DEPOSIT__FORM__SUCCESS,
          attributes: { additionalData: JSON.stringify(analyticsData) },
        });
      } else {
        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
        showTooltip({
          type: TooltipTypes.INFO,
          title: t('cashbox:receivedRequestInfo'),
        });
        handleModal(undefined)();
      }
    },
    [
      prevUsedProfile?.id,
      id,
      currency,
      isDeposit,
      language,
      requestSubmitFormPayment,
      paymentAction,
      sendEventToAnalytics,
      fetchUser,
      t,
      handleModal,
    ]
  );

  return (
    <>
      <Form<MethodFormApi2>
        id="depositForm"
        getApi={(api) => {
          formApiRef.current = api;
        }}
        initialValues={initialValues}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onSubmitFailure={handleSubmitFailure}
      >
        {isDeposit && <Analytics />}
        {amountField && (
          <PaymentField
            {...({
              ...amountField,
              fieldName: 'amountValue',
              paymentAction,
              minAmountValue,
            } as PaymentFieldProps)}
          />
        )}
        {methodFields ? (
          methodFields
            .map(
              ({ fieldName, ...m }) =>
                ({
                  fieldName: `methodFieldsData.${fieldName}`,
                  ...m,
                  paymentAction,
                } as PaymentFieldProps)
            )
            .map((field, index) => <PaymentField key={index} {...field} />)
        ) : (
          <ScNotFound>
            <Trans i18nKey="common:deposits.tryToDepositViaProfile">
              Please try to{' '}
              <Link href="/profile/wallet/deposit">Deposit via profile</Link>
            </Trans>
          </ScNotFound>
        )}
        <p className="amount-wrapper">
          {isWithdraw && <ProcessingTime processingTime={processingTime} />}
          {isCryptoMethod && isDeposit ? (
            <>
              <Trans
                i18nKey="deposits:fields.crypto.depAmount"
                components={{ b: <b /> }}
                tOptions={{ minAmountValue, currency, userCurrency }}
              />
            </>
          ) : (
            isWithdraw && (
              <div className="deposit-value">
                {' '}
                {minAmountValue && <>min {minAmountValue}</>}
                {maxAmountValue && <>, max {maxAmountValue}</>}, {t('payment')}{' '}
                {currency}
              </div>
            )
          )}
        </p>
        {isWithdraw && (
          <div className="withdraw-button">
            <Button
              green
              large
              wide
              type="submit"
              pending={pendingFormPayment}
              disabled={withdrawSubmitDisabled}
            >
              {t('cashbox:withdraw')}
            </Button>
          </div>
        )}

        {isSubmitAvailable && (
          <>
            {prevUsedProfile?.isRemoveAvailable && (
              <div
                className="remove-btn"
                onClick={() => toggleConfirmRemoveModal(true)}
              >
                {t('deposits:remove')}
              </div>
            )}
          </>
        )}
      </Form>

      {isConfirmRemoveModalOpen && (
        <ConfirmRemoveMethodModal
          onClose={() => toggleConfirmRemoveModal(false)}
          currency={currency}
        />
      )}
    </>
  );
};

type ProcessingTimeProps = Pick<TermsOfService, 'processingTime'>;

const ProcessingTime: FC<ProcessingTimeProps> = ({ processingTime }) => {
  const { t } = useTranslation('deposits');

  return (() => {
    switch (processingTime.type) {
      case ProcessingTimeType.Instant:
        return <>{t('instantly')}, </>;
      case ProcessingTimeType.Static:
        return <>{processingTime.days} days, </>;
      case ProcessingTimeType.Range:
        return (
          <>
            {processingTime.minDays} - {processingTime.maxDays} days,{' '}
          </>
        );
      case ProcessingTimeType.None:
      default:
        return null;
    }
  })();
};

const Analytics = () => {
  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);

  const { sendEventToAnalytics } = useAnalytics();

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__FORM__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
